import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { getDate, format } from 'date-fns';
import groupBy from 'lodash/groupBy';
import styled from '@emotion/styled';
import Button from '@atlaskit/button';
import Select, { ValueType } from '@atlaskit/select';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import { Radio } from '@atlaskit/radio';
import { routePaths } from '@css-xp/ppc-constants';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { getFormattedDateString, InfoPanel, DynamicResultTable } from '@css-xp/base';
import { emptyStateProp, NoData } from '@css-xp/ppc-common';
import { CustomPanel } from '@css-xp/ppc-purchase-experience';
import { useStore } from '../../../../store';
import { SelectType } from '../../../billing-profile/src/types';
import { CartLinkedEntitlementsSchema, CartProductSchema } from '../../../../types';
import { LinkedEntitlements } from './linked-entitlements';

export enum MatchBillDatesContentEnum {
  HEADING = 'HEADING',
  NOTIFICATION_SUCCESS_HEADING = 'NOTIFICATION_SUCCESS_HEADING',
  SUBMIT_BUTTON = 'SUBMIT_BUTTON',
}

export type ContentString = {
  [key in MatchBillDatesContentEnum]?: string;
};

type Props = {
  contentString?: ContentString;
  transactionAccoundId?: string;
  closeSidebar: () => void;
  checkedItemMap: Record<string, SelectType>;
  setOnSuccessDateChange: (
    value: {
      title?: string;
      description: string;
    } | null
  ) => void;
  linkedProducts?: Record<string, CartProductSchema>;
  linkedApps?: Record<string, CartLinkedEntitlementsSchema>;
};

export interface SelectItem {
  label: number;
  value: number;
  dateFromSuggestion?: string;
}

const StyledMatchBillDatesContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

const StyledTitle = styled.label({
  color: '#172B4D',
  fontSize: '20px',
  fontWeight: 510,
  lineHeight: '24px',
});

const StyledInnerText = styled.label({
  color: '#172B4D',
  fontSize: '14px',
  fontWeight: 590,
  lineHeight: '16px',
});

const StyledDateSelection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '24px',
});

const StyledDateSuggestionContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '65px',
});

const StyledDateSuggestionPanel = styled.div`
  > div {
    padding: 8px;
    border-radius: 3px 3px 0px 0px;
    background-color: #f1f2f4;
  }
`;

const StyledSelectOption = styled.div({
  width: '201px',
  zIndex: 2,
});

const StyledSuggestionPanelHeading = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const StyledSuggestionPanelList = styled.div`
  display: flex;
  flex-direction: row;
  > label {
    align-items: center;
  }
`;

const StyledProratedAdjustmentText = styled.div({
  paddingTop: '16px',
  textAlign: 'right',
});

const StyledFooter = styled.div({
  display: 'flex',
  gap: '8px',
  justifyContent: 'end',
  padding: '8px 0px 24px 24px',
});

export const getTimestampFromDate = (selectedDate: ValueType<SelectItem, false>) => {
  if (!selectedDate) {
    return '';
  }
  const selectedDateOfMonth = selectedDate.value;
  const currentDateOfMonth = Number(moment().format('DD'));
  const currentTimestamp = moment().valueOf();

  if (selectedDateOfMonth === currentDateOfMonth) {
    return String(moment().valueOf());
  }
  if (selectedDateOfMonth < currentDateOfMonth) {
    return String(
      moment
        .utc(currentTimestamp)
        .subtract(currentDateOfMonth - selectedDateOfMonth, 'days')
        .add(1, 'months')
        .valueOf()
    );
  } else {
    return String(
      moment
        .utc(currentTimestamp)
        .add(selectedDateOfMonth - currentDateOfMonth, 'days')
        .valueOf()
    );
  }
};

export const MatchBillDates: React.FC<Props> = ({
  contentString,
  transactionAccoundId,
  closeSidebar,
  checkedItemMap,
  setOnSuccessDateChange,
  linkedProducts = {},
  linkedApps = {},
}) => {
  const [{ products }, actions] = useStore();
  const [selectedDate, setSelectedDate] = useState<ValueType<SelectItem, false>>();

  const submitMatchBillDates = () => {
    actions.setNextBillAnchorDate(checkedItemMap, getTimestampFromDate(selectedDate), linkedProducts);
    setOnSuccessDateChange({ title: 'New bill date added', description: 'It will be set when you confirm the order.' });
    closeSidebar();
  };

  const handleDateSelectionViaSuggestion = (timeStamp: string) => {
    const dateFromTimeStamp = getFormattedDateString({ date: timeStamp, format: 'DD' });
    setSelectedDate({
      label: Number(dateFromTimeStamp),
      value: Number(dateFromTimeStamp),
      dateFromSuggestion: timeStamp,
    });
  };

  const getProductsDetailGroupedByNextBillDate = useMemo(() => {
    // Group products from store by next bill date
    const groupedProducts = groupBy(
      Object.values(products).map((product) => ({
        ...product,
        nextBillDateFormatted: getFormattedDateString({ date: product.nextBillDate }),
      })),
      'nextBillDateFormatted'
    );
    // Remove keys with empty string or null. For new product added in cart nextBillDate is empty.
    const filteredGroupedProducts = Object.fromEntries(
      Object.entries(groupedProducts).filter(
        ([key]) => key !== 'null' && key.trim() !== '' && getDate(Date.parse(key)) <= 28
      )
    );
    // Sort the grouped products by date
    const sortedFilteredGroupedProducts = Object.fromEntries(
      Object.entries(filteredGroupedProducts).sort((a, b) => (new Date(a[0]) > new Date(b[0]) ? 1 : -1))
    );

    return sortedFilteredGroupedProducts;
  }, [products]);

  const otherSubscriptionsDateSuggestion = Object.keys(getProductsDetailGroupedByNextBillDate).map(
    (productDetailForDate) => {
      const numberOfSubscriptions = getProductsDetailGroupedByNextBillDate[productDetailForDate].length;

      const heading = (
        <StyledSuggestionPanelHeading>
          <Button
            onClick={() => handleDateSelectionViaSuggestion(productDetailForDate)}
            appearance="default"
            iconBefore={<CalendarIcon size="small" label="" />}
          >
            {productDetailForDate}
          </Button>

          <label>
            {numberOfSubscriptions + ' ' + (numberOfSubscriptions === 1 ? 'subscription' : 'subscriptions')}
          </label>
        </StyledSuggestionPanelHeading>
      );

      const wrapInCell = (node?: React.ReactElement | string | null) => {
        return <>{node}</>;
      };

      const makeRows = () => {
        const entitlementBaseRoute = getRouteUrl(routePaths.subscriptions, { txaId: transactionAccoundId || '' });
        return getProductsDetailGroupedByNextBillDate[productDetailForDate].map((entitlement) => {
          const { entitlementNumber, name, productId } = entitlement;
          const rowKey = `entitlement-${productId}`;
          return {
            key: rowKey,
            cells: [
              {
                content: wrapInCell(name ?? 'Unnamed product'),
              },
              {
                content: wrapInCell(
                  <Link
                    to={`${entitlementBaseRoute}${entitlementNumber}`}
                    data-testId="bill-date-entitlement-link"
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {entitlementNumber || <NoData />}
                  </Link>
                ),
              },
            ],
          };
        });
      };
      return (
        <StyledSuggestionPanelList key={`key-${getFormattedDateString({ date: productDetailForDate, format: 'DD' })}`}>
          <Radio
            isChecked={selectedDate?.dateFromSuggestion === productDetailForDate}
            onChange={() => handleDateSelectionViaSuggestion(productDetailForDate)}
            name={getFormattedDateString({ date: productDetailForDate, format: 'DD' }) ?? ''}
            value={getFormattedDateString({ date: productDetailForDate, format: 'DD' }) ?? ''}
          />
          <StyledDateSuggestionPanel>
            <CustomPanel heading={heading} toggleButtonOnLeft noHoverBackground>
              <DynamicResultTable
                head={{ cells: [] }}
                rows={makeRows()}
                loadingSpinnerSize="small"
                emptyStateProps={emptyStateProp}
                boxShadow={false}
              ></DynamicResultTable>
            </CustomPanel>
          </StyledDateSuggestionPanel>
        </StyledSuggestionPanelList>
      );
    }
  );

  const options = () => {
    const daysInMonthList = [];
    for (let i = 1; i <= 28; i++) {
      daysInMonthList.push({ label: i, value: i });
    }
    return daysInMonthList;
  };

  const getDateOfMonth = (date: string) => {
    return format(Number(date), 'do');
  };

  const infoPanelNewSelectedDate = (
    <>
      The new billing date will take effect from the{' '}
      <strong> {getDateOfMonth(getTimestampFromDate(selectedDate))} of next billing cycle</strong>.
    </>
  );

  const isLinkedProductsOrApps = React.useMemo(() => {
    if (Object.keys(linkedApps).length > 0 || Object.keys(linkedProducts).length > 0) {
      return true;
    }
    return false;
  }, [linkedApps, linkedProducts]);

  return (
    <StyledMatchBillDatesContainer>
      <StyledTitle>{contentString && contentString[MatchBillDatesContentEnum.HEADING]}</StyledTitle>
      <StyledDateSelection>
        <StyledInnerText>Select a day of each month to be billed</StyledInnerText>
        <StyledSelectOption data-testid="select-date">
          <Select
            onChange={(selectedOption) => {
              setSelectedDate(selectedOption);
            }}
            options={options()}
            placeholder={'Select'}
            isSearchable
            value={selectedDate}
            blurInputOnSelect={true}
          />
        </StyledSelectOption>
        {selectedDate?.value && <InfoPanel type="information" message={infoPanelNewSelectedDate} />}
      </StyledDateSelection>
      {isLinkedProductsOrApps && <LinkedEntitlements linkedProducts={linkedProducts} linkedApps={linkedApps} />}
      <StyledDateSuggestionContainer>
        <StyledInnerText>Or select a date that matches other subscriptions:</StyledInnerText>
        {otherSubscriptionsDateSuggestion}
      </StyledDateSuggestionContainer>
      {isLinkedProductsOrApps && (
        <StyledProratedAdjustmentText>
          Pro-rated adjustments will be applied to the next invoice.
        </StyledProratedAdjustmentText>
      )}
      <StyledFooter>
        <Button testId="cancel-cta" onClick={closeSidebar} appearance="subtle">
          Cancel
        </Button>
        <Button
          testId="confirm-cta"
          appearance="primary"
          type="submit"
          isDisabled={!selectedDate?.value}
          onClick={submitMatchBillDates}
        >
          {contentString && contentString[MatchBillDatesContentEnum.SUBMIT_BUTTON]}
        </Button>
      </StyledFooter>
    </StyledMatchBillDatesContainer>
  );
};
